import { Injectable, NgZone } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Marker, MarkerClusterer } from '@googlemaps/markerclusterer';
import { forkJoin, Observable, Subject } from 'rxjs';

import { KHThemeService } from '..';
import { environment } from '../../../environments/environment';
import { App, FilterMenuOptions, GeoJsonFeature, KHMapMarker, Point, Position, Project, Site } from '../../models';
import { MapIcon, MapPin } from '../../models/map-pin-model';
import { MAP_DEFAULTS } from '../../shared/constants';
import { MAP_LAYER_Z_INDEXES } from '../../shared/constants/map-layer-z-indexes';
import { GeoJsonFeatureType, MapPinColor, SiteTypeNames, SiteTypes, UserSelectablePresetColors } from '../../shared/enums';
import { MapLayerEnum } from '../../shared/enums/map-layer-type.enum';
import { MapDataService, MapFiltersDataService } from '../data-services';
import { ColorsUtilityService, ContextLayersUtilityService, MapClusterUtilityService } from '../utility-services';
import { MapContextLayer } from './../../models/map-context-layer';
import { SiteMapLogicServiceModel } from './models/map';

@Injectable({
    providedIn: 'root'
})
export class SiteMapLogicService {
    private _sitesAddedToMap: Subject<boolean> = new Subject<boolean>();
    private _contextLayersAddedToMap: Subject<boolean> = new Subject<boolean>();
    private _evaluatedMapPinsReady = new Subject<boolean>();
    private _listenerMap = new WeakMap<object, Set<string>>();
    private _listenerHandlers: google.maps.MapsEventListener[] = [];
    private _infoWindowWheelListeners: { infoWindow: google.maps.MapsEventListener[]; pinnedInfoWindow: google.maps.MapsEventListener[]; } = {
        infoWindow: [],
        pinnedInfoWindow: []
    };

    private _mapStylesChanged: Subject<boolean> = new Subject<boolean>();

    contextLayersAddedToMap$ = this._contextLayersAddedToMap.asObservable();
    sitesAddedToMap$ = this._sitesAddedToMap.asObservable();
    evaluatedMapPinsReady$ = this._evaluatedMapPinsReady.asObservable();
    mapStylesChanged$ = this._mapStylesChanged.asObservable();

    model: SiteMapLogicServiceModel = new SiteMapLogicServiceModel();

    constructor(
        private _contextLayersUtilityService: ContextLayersUtilityService,
        private _mapClusterUtilityService: MapClusterUtilityService,
        private _mapFiltersDataService: MapFiltersDataService,
        private _snackBar: MatSnackBar,
        private _formBuilder: UntypedFormBuilder,
        private _mapDataService: MapDataService,
        private _themeService: KHThemeService,
        private _colorsUtilityService: ColorsUtilityService,
        private _ngZone: NgZone,
    ) { }

    initModel(): void {
        if (this.model === undefined) {
            this.model = new SiteMapLogicServiceModel();
        }
        if (this.model.mapFilterMenuOptions === undefined) {
            this.model.mapFilterMenuOptions = new FilterMenuOptions();
        }
        this.model.mapLayersForm = this._formBuilder.group({
            terrain: [false],
            roadNames: [true],
            pointsOfInterest: [false]
        });
    }

    resetMapMarkers(): void {
        this.model.allMapPins = [];
        this.model.markers = [];
    }

    private resetLatLongBounds(): void {
        this.model.latitudeLongitudeBounds = new google.maps.LatLngBounds();
    }

    resetLatLongBoundsToUS(): void {
        const usBounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(24.396308, -125.0),  // Southwest corner (approx Key West, FL / Pacific)
            new google.maps.LatLng(49.384358, -66.93457) // Northeast corner (approx Maine)
        );

        this.model.latitudeLongitudeBounds = usBounds;
    }

    createMap(mapElement: any): void {
        this.model.map = new google.maps.Map(mapElement, this.model.mapProperties);
        this.model.markerCluster = this._mapClusterUtilityService.createMarkerCluster(this.model.map);
        this.setMapLoading(false);
    }

    clearMap(): void {
        this.clearListeners();
        this.model.map = null;
        this.model.markers = [];
    }

    private fitMapToLatLongBoundsOfSite(site: Site): void {
        this.resetLatLongBounds();
        this.createLatLongBounds([site]);
        this.fitMapToLatLongBounds();
    }

    panSiteOffCenterOfMap(site: Site): void {
        if (!site.latitude || !site.longitude || !this.model.map) {
            return;
        }

        const latLng = this.createGoogleLatLongFromSite(site);
        const projection = this.model.map.getProjection();

        if (!projection) {
            this.model.map.panTo(latLng);
            return;
        }

        const mapWidth = this.model.map.getDiv().offsetWidth;
        const zoom = this.model.map.getZoom();
        const scale = Math.pow(2, zoom);

        // Place the site at 1/3 from the left edge.
        // The center is at mapWidth/2; the target position is mapWidth/3.
        // The difference is mapWidth/6 pixels — shift the center that far to the right.
        const worldPoint = projection.fromLatLngToPoint(latLng);
        const offsetX = mapWidth / 6 / scale;
        const newCenter = projection.fromPointToLatLng(new google.maps.Point(worldPoint.x + offsetX, worldPoint.y));

        this.model.map.panTo(newCenter);
    }

    centerMapOnSite(site: Site): void {
        if (!site.latitude || !site.longitude || !this.model.map) {
            return;
        }
        const latLng = this.createGoogleLatLongFromSite(site);
        this.model.map.panTo(latLng);
    }

    resetMapToPreviousBounds(previousBounds: google.maps.LatLngBounds): void {
        if (!this.model.map || !previousBounds) {
            return;
        }

        this.model.latitudeLongitudeBounds = previousBounds;
        this.fitMapToLatLongBounds();
    }

    private fitMapToLatLongBounds(): void {
        this.model.map?.fitBounds(this.model.latitudeLongitudeBounds);
    }

    private createLatLongBounds(sites: Site[]): void {
        if (sites.length === 0) {
            this.resetLatLongBoundsToUS();
            return;
        }

        sites.forEach((site: Site) => {
            if (site.siteType === SiteTypes.LineArea) {
                this.extendLatLongBoundsFromGeoJson(site);
                return;
            }

            if (!site.latitude || !site.longitude) {
                return;
            }

            const latlng: google.maps.LatLng = this.createGoogleLatLongFromSite(site);
            if (this.isValidBounds(latlng)) {
                this.model.latitudeLongitudeBounds.extend(latlng);
            }
        });
    }

    extendLatLongBoundsFromGeoJson(site: Site): void {
        if (!site.geoJson) {
            return;
        }
        const geoJsonFeature: GeoJsonFeature = site.geoJson;

        this.extendLatLongBoundsFromGeoJsonFeature(geoJsonFeature);
    }

    extendLatLongBounds(coordinate: Position): void {
        const latLng = this.createGoogleLatLong(coordinate[1], coordinate[0]);
        if (this.isValidBounds(latLng)) {
            this.model.latitudeLongitudeBounds.extend(latLng);
        }
    }

    isValidBounds(latlng: google.maps.LatLng): boolean {
        return latlng && !isNaN(latlng.lat()) && !isNaN(latlng.lng());
    }

    createGoogleLatLongFromSite(site: Site): google.maps.LatLng {
        const latitude = site.latitude;
        const longitude = site.longitude;
        return this.createGoogleLatLong(latitude, longitude);
    }

    createGoogleLatLong(latitude: number, longitude: number): google.maps.LatLng {
        return new google.maps.LatLng(latitude, longitude);
    }

    getClusterCenter(cluster: any): google.maps.LatLng {
        if (typeof cluster.getCenter === 'function') {
            return cluster.getCenter();
        }

        if (cluster.bounds) {
            return cluster.bounds.getCenter();
        }

        if (cluster.markers) {
            let lat = 0, lng = 0;
            cluster.markers.forEach((marker: any) => {
                const position = marker.getPosition();
                lat += position.lat();
                lng += position.lng();
            });
            return new google.maps.LatLng(lat / cluster.markers.length, lng / cluster.markers.length);
        }

        return this.model.map.getCenter();
    }

    setMapLoading(isLoading: boolean): void {
        this.model.mapLoading = isLoading;
    }

    setMapFilterVisible(bool: boolean): void {
        this.model.mapFilterVisible = bool;
    }

    createMapPinsAndGeoJson(mapIcon: MapIcon, sites: Site[]): void {
        if (!sites) {
            return;
        }
        this.resetMapMarkers();
        this.clearNonContextLayersGeoJsonFiles();
        for (const site of sites) {
            if (site.siteType === SiteTypes.LineArea) {
                if (!site.geoJson) {
                    console.warn(`${site.siteName} of type ${SiteTypeNames.LineArea} missing geoJson data`);
                    continue;
                }

                if (site.geoJson.geometry.type !== GeoJsonFeatureType.Point) {
                    this.loadSiteGeoJsonFileOnMap(site);
                }
            }
            const mapPin: MapPin = this.createMapPinFromSite(site, mapIcon);
            this.model.allMapPins.push(mapPin);
        }
        this.model.filteredMapPins = [...this.model.allMapPins];
        this.addMarkersToMap();
        if ((sites.length == 1 && this.model.lastCenteredPinSiteId !== sites[0].id)) {
            this.fitMapToLatLongBoundsOfSite(sites[0]);
        }
        else if (sites.length > 1 || sites.length === 0) {
            this.resetLatLongBounds();
            this.createLatLongBounds(sites);
            this.fitMapToLatLongBounds();
        }
        this._sitesAddedToMap.next(true);
    }

    addMarkersToMap(): void {
        this.clearMarkers();
        this.createMarkersFromFilteredMapPins();
    }

    createMarkersFromFilteredMapPins(): void {

        if (this.model.filteredMapPins.length === 0) {
            this.addMarkers();
            return;
        }

        const markerObservables = this.model.filteredMapPins.map(mapPin => this.createMarker(mapPin));

        forkJoin(markerObservables).subscribe(markers => {
            markers.forEach((marker, index) => {
                const mapPin = this.model.filteredMapPins[index];
                this.model.markers.push(marker);
                this.setMarkerAndMapPin(marker, mapPin);
            });

            this.addMarkers();
        });
    }

    createMarker(mapPin: MapPin): Observable<KHMapMarker> {
        const googleLatLng: google.maps.LatLng = this.createGoogleLatLong(mapPin.latitude, mapPin.longitude);
        return KHMapMarker.create$(
            mapPin.id,
            {
                position: googleLatLng,
                label: "",
                map: null,  //let clusterer add markers to map to avoid duplication
                icon: mapPin.icon?.url !== '' ? mapPin.icon : null,
                title: mapPin.label,
                zIndex: MAP_LAYER_Z_INDEXES.Marker
            }
        );
    }

    setMarkerAndMapPin(marker: KHMapMarker, mapPin: MapPin): void {
        this.setMarker(marker);
        this.setMapPin(mapPin);
    }

    setMarker(marker: KHMapMarker): void {
        this.model.marker = marker;
    }

    setMapPin(mapPin: MapPin): void {
        this.model.mapPin = mapPin;
    }

    createInfoWindowContent(site: Site, siteAlias: string, isSiteDataPreviewDialogVisible: boolean): HTMLElement {
        const title: string = site.siteName;
        const siteNo: string = site.siteNo;

        const boxText = document.createElement("div");
        boxText.classList.add('kh-hover-cursor-pointer');
        boxText.innerHTML = (site.active) ? this.generateActiveContent(title, siteNo, siteAlias, isSiteDataPreviewDialogVisible) : this.generateInactiveContent(title, siteNo, siteAlias, isSiteDataPreviewDialogVisible);
        return boxText;
    }

    createContextLayerInfoWindowContent(mapContextLayer: MapContextLayer): HTMLElement {
        const boxText = document.createElement("div");
        boxText.classList.add('kh-hover-cursor-pointer');
        boxText.innerHTML = this.generateMapContextLayerContent(mapContextLayer);
        return boxText;
    }

    generateMapContextLayerContent(mapContextLayer: MapContextLayer): string {
        return `
        <div style="display: flex; flex-direction: row; align-items: center; position: relative;">
            <div style="display: flex; flex-direction: row; align-items: flex-start; margin-right: .5rem; height: 14px; width: 24px; background-color: ${this._colorsUtilityService.model.mapContextLayersFillColors.get(mapContextLayer.fillColor).color}${mapContextLayer.fillOpacity == 1 ? '' : mapContextLayer.fillOpacity * 100}; border: 2px solid ${this._colorsUtilityService.model.mapContextLayersFillColors.get(mapContextLayer.strokeColor).color};">
            </div>
            <div style="display: flex; flex-direction: column; align-items: flex-start; position: relative;">
                <span style="color:#353A40;font-size:larger;font-weight:500;">${mapContextLayer.name}</span>
            </div>
        </div>`;
    }

    generateActiveContent(title: string, siteNo: string, siteAlias: string, isSiteDataPreviewDialogVisible: boolean): string {
        let navigateIcon = '';
        if (isSiteDataPreviewDialogVisible) {
            navigateIcon = `<div class="kh-navigate-icon-wrapper">
                    <button class="mat-focus-indicator mat-mdc-icon-button mat-mdc-button-base" >
                        <span class="mat-button-wrapper">
                            <mat-icon class="material-symbols-outlined kh-info-window-icon">arrow_right_alt</mat-icon>
                        </span>
                    </button>
                </div>`;
        }
        else {
            navigateIcon = "";
        }

        return `
        <div style="display: flex; flex-direction: row; align-items: flex-start; width: 100%;">
            <div style="display: flex; flex-direction: column; align-items: flex-start; position: relative;">
            <span style="color:#353A40;font-size:larger;font-weight:500; padding-bottom:.5rem;">${title}</span>
                <span style="color:#77787B;">${siteAlias} No: ${siteNo}</span>
            </div>
            ${navigateIcon}
        </div>`;
    }

    generateInactiveContent(title: string, siteNo: string, siteAlias: string, isSiteDataPreviewDialogVisible: boolean): string {
        let navigateIcon = '';
        if (isSiteDataPreviewDialogVisible) {
            navigateIcon = `<div class="kh-navigate-icon-wrapper">
                    <button class="mat-focus-indicator mat-mdc-icon-button mat-mdc-button-base" >
                        <span class="mat-button-wrapper">
                            <mat-icon class="material-symbols-outlined kh-info-window-icon">arrow_right_alt</mat-icon>
                        </span>
                    </button>
                </div>`;
        }
        else {
            navigateIcon = "";
        }

        return `
            <div style="display: flex; flex-direction: row; align-items: flex-start; width: 100%;">
            <div style="display: flex; flex-direction: column; align-items: flex-start; position: relative;">
            <span style="color:#353A40;font-size:larger;font-weight:500; padding-bottom:.5rem;">${title}</span>
                <span style="color:#77787B;">${siteAlias} No: ${siteNo}</span>
                <span style="font-style:italic">(Inactive)</span>
            </div>
            ${navigateIcon}
        </div>`;
    }

    clearMarkers(): void {
        if (this.model.markers) {
            this.model.markers.forEach(marker => {
                if (marker.getMap()) {
                    marker.setMap(null);
                }
            });
            this.model.markers = [];
        }

        if (this.model.markerCluster) {
            this.model.markerCluster.clearMarkers();
        }
    }

    addMarkers(): void {
        if (this.model.markerCluster && this.model.markers) {
            const googleMarkers: Marker[] = this.model.markers.map(m => {
                m.marker.id = m.id;
                return m.marker;
            });
            this.model.markerCluster.addMarkers(googleMarkers); //since using clusterer, map is set to null on createMarker() and clusterer handle showing on map

            this.model.markerCluster.render();
        }
    }

    createMapPinFromSite(site: Site, defaultMapIcon: MapIcon): MapPin {
        let mapPinIcon: MapIcon = defaultMapIcon;
        if (this.model.evaluatedMapPinColorsBySiteId.get(site.id)) {
            const mapPinColor: MapPinColor = this.model.evaluatedMapPinColorsBySiteId.get(site.id);
            const mapPinColorAsHex = this._colorsUtilityService.getHexOfMapPinColor(mapPinColor);
            const svg = this._themeService.buildColorizedSvgFromHex(mapPinColorAsHex);
            mapPinIcon = new MapIcon(svg, this._themeService.getMapPinScaledSize());
        }
        return new MapPin(mapPinIcon, site);
    }

    getMapFilterOptions(app: App, project: Project): void {
        if (project?.id === undefined) {
            return;
        }

        const mapFilterMenu$ = project.isSummaryProject
            ? this._mapFiltersDataService.getMapFilterMenuBySummaryProjectId(app, project)
            : this._mapFiltersDataService.getMapFilterMenuByProjectId(app, project);
        mapFilterMenu$
            .subscribe({
                next: (mapFilterMenuOptions: FilterMenuOptions) => {
                    if (mapFilterMenuOptions) {
                        mapFilterMenuOptions.allFields = mapFilterMenuOptions.selectedFields;
                        this.model.mapFilterMenuOptions = mapFilterMenuOptions;
                        this.model.mapFilterVisible = true;
                    }
                    else {
                        this.model.mapFilterMenuOptions = new FilterMenuOptions();
                        this.model.mapFilterVisible = false;
                    }
                },
                error: () => {
                    this.model.mapFilterMenuOptions = new FilterMenuOptions();
                    this._snackBar.open(`Error getting Map Filters`, `Close`, { duration: 5000 });
                }
            });
        return;
    }

    adjustLatLongBounds(sites: Site[]): void {
        this.resetLatLongBounds();
        this.createLatLongBounds(sites);
        this.fitMapToLatLongBounds();
    }

    applyMapFilters(app: App, project: Project, mapFilterMenuOptions: FilterMenuOptions, mapIcon: MapIcon, sites: Site[]): void {
        if (sites?.length === 0) {
            return;
        }
        this.clearNonContextLayersGeoJsonFiles();

        if (mapFilterMenuOptions.selectedFields.length === 0) {
            if (sites.length > 0 || sites[0].siteType === SiteTypes.LineArea) {
                this.model.filteredMapPins = [...this.model.allMapPins];
                this.addMarkersToMap();
                this.addFilteredGeoJsonSitesToMap(sites.filter(site => site.siteType === SiteTypes.LineArea));
                this.adjustLatLongBounds(sites);
            }

            this._sitesAddedToMap.next(true);

            this._snackBar.open(`Map Filters Removed`, `Close`, { duration: 3000 });
            return;
        }
        this.model.mapLoading = true;
        this.getSitesByFilterValues(app, project, mapFilterMenuOptions, sites);
    }

    getSitesByFilterValues(app: App, project: Project, mapFilterMenuOptions: FilterMenuOptions, sites: Site[]): void {
        const filteredSiteIds$ = project.isSummaryProject
            ? this._mapFiltersDataService.getSitesByFilterValuesBySummaryProjectId(app, project, mapFilterMenuOptions)
            : this._mapFiltersDataService.getSitesByFilterValues(app, project, mapFilterMenuOptions);
        filteredSiteIds$
            .subscribe({
                next: (filteredSitesIds: string[]) => {
                    const filteredSites = sites.filter(site => filteredSitesIds.includes(site.id));
                    this.model.filteredMapPins = [];
                    this.model.filteredMapPins = this.model.allMapPins.filter(maPin => filteredSitesIds.includes(maPin.id.toString()));

                    this.addMarkersToMap();
                    this.addFilteredGeoJsonSitesToMap(filteredSites.filter(site => site.siteType === SiteTypes.LineArea));
                    this._sitesAddedToMap.next(true);

                    if (filteredSites.length > 0) {
                        this.adjustLatLongBounds(filteredSites);
                    }

                    if (filteredSitesIds.length === 1) {
                        this._snackBar.open(`Map Filters Applied. 1 ${app.aliases.site.singular} Found!`, `Close`, { duration: 3000 });
                        return;
                    }

                    this._snackBar.open(`Map Filters Applied. ${filteredSitesIds.length} ${app.aliases.site.plural} Found!`, `Close`, { duration: 3000 });
                },
                error: () => {
                    this._snackBar.open(`Error Applying Map Filters`, `Close`, { duration: 5000 });
                },
                complete: () => {
                    this.model.mapLoading = false;
                }
            });
    }

    oneSiteDefaultZoom(siteId: string): void {
        if (!siteId) {
            return;
        }

        google.maps.event.addListenerOnce(this.model.map, 'idle', () => {
            this.model.map.setZoom(19);
        });

        this.model.lastCenteredPinSiteId = siteId;
    }

    loadSiteGeoJsonFileOnMap(site: Site): void {
        const geoJsonFeature: GeoJsonFeature = site.geoJson;
        geoJsonFeature.properties.siteId = site.id;

        const dataLayer = new google.maps.Data();
        dataLayer.addGeoJson(geoJsonFeature);
        dataLayer.setStyle(this.getGeoJsonStyle);
        dataLayer.setMap(this.model.map);

        this.model.dataLayersMap.set(site.id, [dataLayer]);
    }

    getGeoJsonStyle(feature: google.maps.Data.Feature): google.maps.Data.StyleOptions {
        return {
            title: (feature.getProperty('siteId') ?? '') as string,
            cursor: 'pointer',
            clickable: true,
            fillOpacity: 0,
            strokeColor: UserSelectablePresetColors.Black,
            strokeOpacity: Math.max(0, Math.min(1, Number(feature.getProperty('strokeOpacity') ?? feature.getProperty('stroke-opacity') ?? 1))),
            strokeWeight: 2,
            zIndex: MAP_LAYER_Z_INDEXES.GeoJsonSiteLayer
        };
    }

    clearNonContextLayersGeoJsonFiles(): void {
        this.model.dataLayersMap.forEach((dataLayers, mapKey) => {
            if (mapKey.startsWith('mapContextLayer')) {
                return;
            }

            dataLayers.forEach((dataLayer) => {
                dataLayer.setMap(null);
            });
            this.model.dataLayersMap.delete(mapKey);
        });
    }

    clearAllGeoJsonFiles(): void {
        this.model.dataLayersMap.forEach((dataLayers) => {
            dataLayers.forEach((dataLayer) => {
                dataLayer.setMap(null);
            });
        });
        this.model.dataLayersMap.clear();
    }

    clearContextLayersGeoJsonFiles(contextLayers: MapContextLayer[]): void {
        contextLayers.forEach((contextLayer) => {
            this.model.dataLayersMap.get(contextLayer.id)?.forEach((dataLayer) => {
                dataLayer.setMap(null);
            });
            this.model.dataLayersMap.delete(contextLayer.id);
        });
    }

    addFilteredGeoJsonSitesToMap(sites: Site[]): void {
        sites.forEach((site) => {
            if (!site.geoJson) {
                return;
            }
            this.loadSiteGeoJsonFileOnMap(site);
        });
    }

    openLayersMenu(): void {
        this.model.expandedLayersMenu = true;
    }

    closeLayersMenu(): void {
        this.model.expandedLayersMenu = false;
    }

    setMapTypeProperty(appMapLayer?: string): void {
        this.model.mapProperties.mapTypeId = appMapLayer ? appMapLayer : MapLayerEnum.RoadMap;
    }

    togglePOI(): void {
        this._updateMapStyleForLayerState();
        this._mapStylesChanged.next(true);
    }

    toggleRoadNames(): void {
        this._updateMapStyleForLayerState();
        this._mapStylesChanged.next(true);
    }

    private _updateMapStyleForLayerState(): void {
        const hasRoadNames = this.model.mapLayersForm.get('roadNames')?.value ?? false;
        const hasPOI = this.model.mapLayersForm.get('pointsOfInterest')?.value ?? false;

        this.model.mapTypeToRevert = this.model.map.getMapTypeId() as MapLayerEnum;
        this.model.mapCenterToRevert = this.model.map.getCenter();
        this.model.mapZoomToRevert = this.model.map.getZoom();
        this.model.mapProperties.mapId = this._getMapIdForLayerState(hasRoadNames, hasPOI);
    }

    private _getMapIdForLayerState(hasRoadNames: boolean, hasPOI: boolean): string {
        if (hasRoadNames && hasPOI) {
            return environment.googleMaps.mapIds.streetLabelsPOI;
        }
        if (hasRoadNames && !hasPOI) {
            return environment.googleMaps.mapIds.streetLabelsNoPOI;
        }
        if (!hasRoadNames && hasPOI) {
            return environment.googleMaps.mapIds.noStreetLabelsPOI;
        }
        return environment.googleMaps.mapIds.noStreetLabelsNoPOI;
    }

    restoreMapTypeToProperties(): void {
        this.model.mapProperties.mapTypeId = this.model.mapTypeToRevert;
    }

    restoreMapZoomAndCenter(): void {
        google.maps.event.addListenerOnce(this.model.map, 'idle', () => {
            if (this.model.mapCenterToRevert) {
                this.model.map.setCenter(this.model.mapCenterToRevert);
            }
            if (this.model.mapZoomToRevert !== undefined && this.model.mapZoomToRevert !== null) {
                this.model.map.setZoom(this.model.mapZoomToRevert);
            }
            this.model.mapCenterToRevert = null;
            this.model.mapZoomToRevert = null;
        });
    }

    switchMapTypeToRoadMap(): void {
        this.model.map.setMapTypeId(MapLayerEnum.RoadMap);
    }

    switchMapTypeToSatellite(): void {
        this.model.map.setMapTypeId(MapLayerEnum.Satellite);
    }

    switchMapTypeToTerrain(): void {
        this.model.map.setMapTypeId(MapLayerEnum.Terrain);
    }

    closeInfoWindow(): void {
        this.clearInfoWindowWheelListeners('infoWindow');
        this.model.infoWindow.close();
    }

    closePinnedInfoWindow(): void {
        this.clearInfoWindowWheelListeners('pinnedInfoWindow');
        this.model.pinnedInfoWindow.close();
    }

    getEvaluatedMapPinColorsBySite(app: App, project: Project): void {
        if (project.isSummaryProject) {
            this.model.evaluatedMapPinColorsBySiteId = new Map<string, MapPinColor>();
            this._evaluatedMapPinsReady.next(true);
            return;
        }

        this._mapDataService.getEvaluatedMapPinColorsBySite(app, project)
            .subscribe({
                next: (result: Record<string, any>) => {
                    this.model.evaluatedMapPinColorsBySiteId = new Map<string, MapPinColor>();
                    for (const key in result) {
                        this.model.evaluatedMapPinColorsBySiteId.set(key, result[key]);
                    }

                    this._evaluatedMapPinsReady.next(true);
                }
            });
    }

    setSiteEvaluatedMapPinColorBySiteId(site: Site, mapPinColor: MapPinColor): void {
        this.model.evaluatedMapPinColorsBySiteId.set(site.id, mapPinColor);
    }

    clearSiteEvaluatedMapPinColorBySiteId(site: Site): void {
        this.model.evaluatedMapPinColorsBySiteId.delete(site.id);
    }

    hasListener(element: any, eventName: string): boolean {
        const listeners = this._listenerMap.get(element);
        return listeners ? listeners.has(eventName) : false;
    }

    markListenerAdded(element: any, eventName: string): void {
        let listeners = this._listenerMap.get(element);
        if (!listeners) {
            listeners = new Set<string>();
            this._listenerMap.set(element, listeners);
        }
        listeners.add(eventName);
    }

    clearListeners(): void {
        this.clearInfoWindowWheelListeners('all');

        this._listenerHandlers.forEach((listener: google.maps.MapsEventListener) => {
            listener.remove();
        });
        this._listenerHandlers = [];
        this._listenerMap = new WeakMap<object, Set<string>>();
    }

    clearInfoWindowWheelListeners(listenerGroup: 'all' | 'infoWindow' | 'pinnedInfoWindow' = 'all'): void {
        if (listenerGroup === 'all' || listenerGroup === 'infoWindow') {
            this._infoWindowWheelListeners.infoWindow.forEach((listener: google.maps.MapsEventListener) => {
                listener.remove();
            });
            this._infoWindowWheelListeners.infoWindow = [];
        }

        if (listenerGroup === 'all' || listenerGroup === 'pinnedInfoWindow') {
            this._infoWindowWheelListeners.pinnedInfoWindow.forEach((listener: google.maps.MapsEventListener) => {
                listener.remove();
            });
            this._infoWindowWheelListeners.pinnedInfoWindow = [];
        }
    }

    addGoogleEventListener(element: any, eventName: string, callback: (...args: any[]) => void): void {
        if (this.hasListener(element, eventName)) {
            return;
        }

        let listener: google.maps.MapsEventListener;
        this.markListenerAdded(element, eventName);
        this._ngZone.run(() => {
            listener = google.maps.event.addListener(element, eventName, callback);
        });
        this._listenerHandlers.push(listener);
    }

    addDomEventListener(element: any, eventName: string, callback: (...args: any[]) => void): void {
        if (this.hasListener(element, eventName)) {
            return;
        }
        let listener: google.maps.MapsEventListener;

        this.markListenerAdded(element, eventName);

        this._ngZone.run(() => {
            listener = google.maps.event.addDomListener(element, eventName, callback);
        });
        this._listenerHandlers.push(listener);
    }

    addCtrlWheelEventListenersToInfoWindow(infoWindowContent: HTMLElement, mapElement?: HTMLElement, listenerGroup: 'infoWindow' | 'pinnedInfoWindow' = 'infoWindow'): void {
        // These listeners are temporary and should only live for the currently opened info window.
        this.clearInfoWindowWheelListeners(listenerGroup);

        const preventCtrlWheel = (event: WheelEvent): void => {
            if (event.ctrlKey) {
                event.preventDefault();
            }
        };

        this._ngZone.runOutsideAngular(() => {
            const contentListener = google.maps.event.addDomListener(infoWindowContent, 'wheel', preventCtrlWheel);
            this._infoWindowWheelListeners[listenerGroup].push(contentListener);

            const wrapperSelectors = [
                '.gm-style-iw-d',
                '.gm-style-iw-c',
                '.gm-style-iw',
                '.gm-style-iw-tc'
            ];

            [0, 50, 150].forEach((delay: number) => {
                setTimeout(() => {
                    wrapperSelectors.forEach((selector: string) => {
                        const wrapper = infoWindowContent.closest(selector) as HTMLElement | null;
                        if (wrapper) {
                            const wrapperListener = google.maps.event.addDomListener(wrapper, 'wheel', preventCtrlWheel);
                            this._infoWindowWheelListeners[listenerGroup].push(wrapperListener);
                        }
                    });

                    // Pseudo-elements (e.g. .gm-style-iw-tc::after) cannot receive listeners,
                    // so bind to their owning element instead.
                    mapElement?.querySelectorAll('.gm-style-iw-tc').forEach((tipContainer: Element) => {
                        const tipListener = google.maps.event.addDomListener(tipContainer as HTMLElement, 'wheel', preventCtrlWheel);
                        this._infoWindowWheelListeners[listenerGroup].push(tipListener);
                    });
                }, delay);
            });
        });
    }

    isUnderCluster(latLng: google.maps.LatLng | null, clustererInstance: MarkerClusterer | null | undefined): boolean {
        if (!latLng || !this.model.map || !clustererInstance) {
            return false;
        }

        const projection = this.model.map.getProjection();
        const zoom = this.model.map.getZoom();
        if (!projection || zoom === undefined) {
            return false;
        }

        const clusterer = clustererInstance as unknown as { clusters?: any[]; };
        const clusters = clusterer.clusters ?? [];
        if (clusters.length === 0) {
            return false;
        }

        const worldScale = Math.pow(2, zoom);
        const hoveredPoint = projection.fromLatLngToPoint(latLng);
        const clusterHitRadiusPx = 30; // hover radius in pixels around the cluster center

        return clusters.some((cluster: any) => {
            if (!cluster?.marker || cluster.count <= 1) {
                return false;
            }

            const clusterPosition = (cluster.position ?? cluster.marker.getPosition()) as google.maps.LatLng | null;
            if (!clusterPosition) {
                return false;
            }

            const clusterPoint = projection.fromLatLngToPoint(clusterPosition);
            const dx = (hoveredPoint.x - clusterPoint.x) * worldScale;
            const dy = (hoveredPoint.y - clusterPoint.y) * worldScale;

            return Math.hypot(dx, dy) <= clusterHitRadiusPx;
        });
    }

    isUnderSiteCluster(latLng: google.maps.LatLng | null): boolean {
        return this.isUnderCluster(latLng, this.model.markerCluster);
    }

    loadGeoJsonContextLayersOnMap(contextLayers: MapContextLayer[], mapSettingsLayersPageLoad: boolean): void {
        if (contextLayers.length > 0) {
            const sortedContextLayers = this._contextLayersUtilityService.sortByOrderDescending(contextLayers);
            sortedContextLayers.reverse();

            if (mapSettingsLayersPageLoad) {
                this.resetLatLongBounds();
            }

            sortedContextLayers.forEach((contextLayer, index) => {
                if (!contextLayer.geoJsonFeatures || !contextLayer.isVisible) {
                    return;
                }

                const layerStyle = this.setContextLayerStyle(contextLayer, index);

                const layers: google.maps.Data[] = [];
                contextLayer.geoJsonFeatures.forEach(geoJsonFeature => {
                    const dataLayer = new google.maps.Data();
                    dataLayer.addGeoJson(geoJsonFeature);
                    if (geoJsonFeature.geometry.type === GeoJsonFeatureType.Point || geoJsonFeature.geometry.type === GeoJsonFeatureType.MultiPoint) {
                        this.addPointLayerStyle(layerStyle);
                    }
                    dataLayer.setStyle(layerStyle);
                    dataLayer.setMap(this.model.map);
                    layers.push(dataLayer);
                    if (mapSettingsLayersPageLoad) {
                        this.extendLatLongBoundsFromGeoJsonFeature(geoJsonFeature);
                    }
                });
                this.model.dataLayersMap.set(contextLayer.id, layers);
            });
            if (mapSettingsLayersPageLoad) {
                this.fitMapToLatLongBounds();
            }
            this._contextLayersAddedToMap.next(true);
        }
    }

    loadGeoJsonPreviewLayerOnMap(formGroup: UntypedFormGroup, geoJsonFeatures: GeoJsonFeature[]): void {
        const layerStyle = this.setContextLayerStyleFromForm(formGroup);

        this.clearAllGeoJsonFiles();
        this.resetLatLongBounds();

        geoJsonFeatures?.forEach((geoJsonFeature, index) => {
            const dataLayer = new google.maps.Data();
            dataLayer.addGeoJson(geoJsonFeature);
            if (geoJsonFeature.geometry.type === GeoJsonFeatureType.Point || geoJsonFeature.geometry.type === GeoJsonFeatureType.MultiPoint) {
                this.addPointLayerStyle(layerStyle);
            }
            dataLayer.setStyle(layerStyle);
            dataLayer.setMap(this.model.map);
            this.model.dataLayersMap.set(index.toString(), [dataLayer]);
            this.extendLatLongBoundsFromGeoJsonFeature(geoJsonFeature);
        });
        this.fitMapToLatLongBounds();
        this._contextLayersAddedToMap.next(true);
    }

    extendLatLongBoundsFromGeoJsonFeature(geoJsonFeature: GeoJsonFeature): void {
        if (!geoJsonFeature) {
            return;
        }
        const geoType = geoJsonFeature.geometry.type;
        switch (geoType) {
            case GeoJsonFeatureType.Point:
                const geometry = geoJsonFeature.geometry as Point;
                this.extendLatLongBounds(geometry.coordinates);
                break;
            case GeoJsonFeatureType.MultiPoint:
            case GeoJsonFeatureType.LineString:
                geoJsonFeature.geometry.coordinates.forEach((coordinate) => {
                    this.extendLatLongBounds(coordinate);
                });
                break;
            case GeoJsonFeatureType.MultiLineString:
            case GeoJsonFeatureType.Polygon:
                geoJsonFeature.geometry.coordinates.forEach((coordinate) => {
                    coordinate.forEach((c) => {
                        this.extendLatLongBounds(c);
                    });
                });
                break;
            case GeoJsonFeatureType.MultiPolygon:
                geoJsonFeature.geometry.coordinates.forEach((coordinate) => {
                    coordinate.forEach((c) => {
                        c.forEach((cc) => {
                            this.extendLatLongBounds(cc);
                        });
                    });
                });
                break;
            default:
                break;
        }
    }

    resetLatLongToContextLayers(contextLayers: MapContextLayer[]): void {
        this.resetLatLongBounds();
        contextLayers.forEach((contextLayer) => {
            contextLayer.geoJsonFeatures?.forEach((geoJsonFeature) => {
                this.extendLatLongBoundsFromGeoJsonFeature(geoJsonFeature);
            });
        });

        this.fitMapToLatLongBounds();
    }

    setContextLayerStyle(contextLayer: MapContextLayer, index: number): google.maps.Data.StyleOptions {
        const fillColorConfig = this._colorsUtilityService.model.mapContextLayersFillColors?.get(contextLayer.fillColor);
        const strokeColorConfig = this._colorsUtilityService.model.mapElementColors?.get(contextLayer.strokeColor);

        const style: google.maps.Data.StyleOptions = {
            fillColor: fillColorConfig?.color,
            fillOpacity: contextLayer.fillOpacity,
            strokeColor: strokeColorConfig?.color,
            strokeWeight: contextLayer.strokeWidth,
            zIndex: MAP_LAYER_Z_INDEXES.ContextLayer + index
        };
        return style;
    }

    addPointLayerStyle(style: google.maps.Data.StyleOptions): void {
        style.icon = {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: style.fillColor,
            fillOpacity: style.fillOpacity,
            strokeWeight: style.strokeWeight,
            strokeColor: style.strokeColor,
        };
    }

    setContextLayerStyleFromForm(formGroup: UntypedFormGroup): google.maps.Data.StyleOptions {
        const fillColorConfig = this._colorsUtilityService.model.mapContextLayersFillColors?.get(formGroup.get('fillColor').value);
        const strokeColorConfig = this._colorsUtilityService.model.mapElementColors?.get(formGroup.get('strokeColor').value);
        const fillOpacityConfig = formGroup.get('fillOpacity').value;
        const strokeWidthConfig = formGroup.get('strokeWidth').value;

        const style: google.maps.Data.StyleOptions = {
            fillColor: fillColorConfig?.color,
            fillOpacity: Number(fillOpacityConfig),
            strokeColor: strokeColorConfig?.color,
            strokeWeight: Number(strokeWidthConfig),
        };
        return style;
    }

    resetMapProperties(): void {
        this.model.mapProperties.zoomControl = false;
        this.model.mapProperties.streetViewControl = true;
        this.model.mapProperties.fullscreenControl = true;
        this.model.mapProperties.tiltInteractionEnabled = true;
        this.model.mapProperties.rotateControl = true;
        this.model.mapProperties.zoom = MAP_DEFAULTS.DEFAULT_MAP_ZOOM;
        this.model.mapProperties.center = MAP_DEFAULTS.DEFAULT_MAP_CENTER;
    }

    initializeWidgetMapProperties(): void {
        this.model.mapProperties.zoomControl = false;
        this.model.mapProperties.streetViewControl = false;
        this.model.mapProperties.tiltInteractionEnabled = false;
        this.model.mapProperties.rotateControl = false;
        this.model.mapProperties.fullscreenControl = false;
    }

    initializeExpandedWidgetMapProperties(): void {
        this.model.mapProperties.zoomControl = true;
        this.model.mapProperties.streetViewControl = true;
        this.model.mapProperties.tiltInteractionEnabled = false;
        this.model.mapProperties.rotateControl = false;
        this.model.mapProperties.fullscreenControl = true;
    }
}
